import { useCallback, useMemo } from 'react';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Separator } from '@givebutter/primitives/separator';
import * as Toast from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import {
    faExclamationCircle,
    faSpinnerThird,
    faXmarkToSlot,
} from '@fortawesome/pro-regular-svg-icons';
import { faSoap } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    BulkIgnoreDataHygieneEmailsParams,
    bulkIgnoreDataHygieneEmails,
    fetchDataHygieneEmails,
    undoBulkIgnoreDataHygieneEmails,
} from 'api/contacts/data-hygiene';
import { CountBadge } from 'assets/js/pages/contacts/data-hygiene/CountBadge';
import { DataHygieneEmailContext } from 'assets/js/pages/contacts/data-hygiene/DataHygieneEmailContext';
import * as EducationalContentDialog from 'assets/js/pages/contacts/data-hygiene/EducationalContentDialog';
import { EmailStatus } from 'assets/js/pages/contacts/data-hygiene/EmailStatus';
import { EmailUpdate } from 'assets/js/pages/contacts/data-hygiene/EmailUpdate';
import { EmailsTrialProgressCard } from 'assets/js/pages/contacts/data-hygiene/EmailsTrialProgressCard';
import { HasSavedTableEmpty } from 'assets/js/pages/contacts/data-hygiene/HasSavedTableEmpty';
import * as IgnoreButton from 'assets/js/pages/contacts/data-hygiene/IgnoreButton';
import { NameTableCell } from 'assets/js/pages/contacts/data-hygiene/NameTableCell';
import { ReviewingYourDataTableEmpty } from 'assets/js/pages/contacts/data-hygiene/ReviewingYourDataTableEmpty';
import { SavedForLaterTableEmpty } from 'assets/js/pages/contacts/data-hygiene/SavedForLaterTableEmpty';
import { ToggleSaveForLaterButton } from 'assets/js/pages/contacts/data-hygiene/ToggleSaveForLaterButton';
import { HYGIENE_PAGE_SIZES } from 'assets/js/pages/contacts/data-hygiene/constants';
import { useToggleSavedForLater } from 'assets/js/pages/contacts/data-hygiene/hooks/useToggleSavedForLater';
import { FilterGroupProvider } from 'components/filter/filters-next/FilterGroupContext';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import { QuickFiltersBar } from 'components/filter/filters-next/QuickFiltersBar';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { ServerKey } from 'components/filter/types';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { RowPendingFeature } from 'components/table/RowPendingFeature';
import { RowReplacementFeature } from 'components/table/RowReplacementFeature';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useDataHygieneMetrics } from 'customHooks/useDataHygieneMetrics';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { isEmpty } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { DataHygieneEmail } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<DataHygieneEmail>();
const allFilters: ServerKey[] = ['invalidEmail', 'bouncedEmail'];

const DataHygieneEmails = () => {
    const [search, setSearch] = usePersistentSearch();
    const debouncedSearch = useDebounce(search);
    const { accountId, dataHygiene, isCurrentlyOnFreeTrial } = useCurrentAccount();
    const { success: toastSuccess, error: toastError } = Toast.useToast();
    const { data: metrics } = useDataHygieneMetrics();

    const { savedForLater, toggleSavedForLater } = useToggleSavedForLater();

    const { filters } = useFiltersByKey('dataHygieneEmails');

    const {
        pagination,
        setPagination,
        rowPending,
        setRowPending,
        rowReplacement,
        setRowReplacement,
    } = Table.useReactTableState({
        pageSize: HYGIENE_PAGE_SIZES[1],
    });

    const [isPending, params] = useIsPending(
        {
            query: debouncedSearch,
            page: pagination.pageIndex + 1,
            per_page: pagination.pageSize,
            eager: true,
            filters,
            options: { saved_for_later: savedForLater },
        },
        500,
    );

    const { data: emails, isFetching } = useSuspenseQuery({
        queryKey: accountQueryKeys.dataHygieneEmailsWithFilters(accountId, params),
        queryFn: () => fetchDataHygieneEmails(accountId, params),
    });

    const { mutate: undoBulkIgnore } = useMutation({
        mutationFn: (values: BulkIgnoreDataHygieneEmailsParams) =>
            undoBulkIgnoreDataHygieneEmails(accountId, {
                ...values,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneEmails(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            toastError(error?.response?.data?.message ?? 'Failed to undo ignore');
        },
    });

    const { mutate: bulkIgnore, isPending: bulkIgnoreIsPending } = useMutation({
        mutationFn: (values: BulkIgnoreDataHygieneEmailsParams) =>
            bulkIgnoreDataHygieneEmails(accountId, values),
        onMutate: values => {
            if (table.options.enableRowPending) {
                table.addRowPending(values.row_ids);
            }

            // NOTE: return the values to be used in context for below handlers
            return { values };
        },
        onSuccess: (data, variables, context) => {
            const { values } = context ?? {};
            const issueCount = values?.ids?.length || 0;

            toastSuccess(
                <div className="flex flex-col">
                    <p className="font-medium text-gray-900">
                        {issueCount > 0 ? (
                            <span>
                                {issueCount} {pluralize(issueCount, 'issue')} ignored
                            </span>
                        ) : (
                            <span>Successfully ignored</span>
                        )}
                    </p>
                    <div className="flex flex-col gap-2">
                        <span className="text-gray-700">We'll remind you again in 90 days.</span>
                        <div>
                            <Toast.Close
                                className="block text-sm font-medium text-blue-500 hover:underline"
                                onClick={() => {
                                    undoBulkIgnore(values);
                                }}
                            >
                                <span>Undo</span>
                            </Toast.Close>
                        </div>
                    </div>
                </div>,
                { duration: 5000, size: 'md' },
            );

            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneEmails(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            toastError(error?.response?.data?.message ?? 'An unknown error occurred');
        },
        onSettled: (data, error, variables, context) => {
            const { values } = context ?? {};

            if (table.options.enableRowPending && values?.row_ids) {
                table.removeRowPending(values.row_ids);
            }
        },
    });

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: Table.Constants.Select,
                header: () => <Table.HeaderCheckbox />,
                cell: () => <Table.RowCheckbox />,
                meta: {
                    ignoreLink: true,
                    className: '!w-4',
                    headerClassName: '!w-4 !pr-0',
                },
            }),
            columnHelper.accessor('contact', {
                header: 'Name',
                meta: {
                    className: '!pl-3 [&>*]:whitespace-normal !w-1/4',
                    headerClassName: '!pl-3 !w-1/4',
                },
                cell: ({ getValue }) => (
                    <NameTableCell contact={getValue()} accountId={accountId} />
                ),
            }),
            columnHelper.accessor('deliverability_status', {
                header: 'Issue',
                meta: {
                    className: '[&>*]:whitespace-normal !w-1/4',
                    headerClassName: '!w-1/4',
                },
                cell: ({ getValue }) => <EmailStatus status={getValue()} />,
            }),
            columnHelper.display({
                id: 'Email',
                header: 'Email',
                meta: {
                    className: '[&>*]:whitespace-normal !w-2/4',
                    headerClassName: '!w-2/4',
                },
                cell: info => {
                    return <EmailUpdateCell email={info.row.original} />;
                },
            }),
        ],
        [accountId],
    );

    const tableSearchTerm = debouncedSearch;
    const isFiltered = !isEmpty(filters);
    const tableData = emails?.data ?? [];
    const emailsResultsCount = emails.meta.total ?? 0;

    const table = useReactTable({
        _features: [RowPendingFeature, RowReplacementFeature],
        data: tableData,
        columns,
        pageCount: emails.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            rowPending,
            rowReplacement,
        },
        onPaginationChange: updater => {
            setPagination(updater);
            table.resetRowSelection();
        },
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
        getRowId: row => row.id.toString(),
        enableRowPending: true,
        onRowPendingChange: setRowPending,
        enableRowReplacement: true,
        onRowReplacementChange: setRowReplacement,
    });

    const selectedRows = table.getSelectedRowModel().flatRows;
    const selectedRowsOriginal = selectedRows.map(row => row.original);

    useSyncPaginatedData(table, emails);

    const DerivedTableEmpty = useCallback(() => {
        if (savedForLater) {
            return <SavedForLaterTableEmpty className="max-w-md" />;
        } else if (metrics.data_hygiene.emails.saved_for_later > 0) {
            return (
                <HasSavedTableEmpty
                    className="max-w-md"
                    toggleSavedForLater={toggleSavedForLater}
                />
            );
        }

        if (
            isCurrentlyOnFreeTrial &&
            dataHygiene.free_trial_scans?.emails.deliverability.is_processing
        ) {
            return <ReviewingYourDataTableEmpty />;
        }

        return (
            <Table.Empty>
                <Table.EmptyIcon>
                    <FontAwesomeIcon icon={faSoap} />
                </Table.EmptyIcon>
                <Table.EmptyTitle>Looks clean!</Table.EmptyTitle>
                <Table.EmptyDescription>
                    All email addresses attached to contacts don't require correction.
                </Table.EmptyDescription>
            </Table.Empty>
        );
    }, [metrics, savedForLater, toggleSavedForLater, dataHygiene, isCurrentlyOnFreeTrial]);

    return (
        <div className="flex flex-col gap-4">
            <EmailsTrialProgressCard />
            <div className="flex flex-col gap-1">
                <div className="flex flex-wrap justify-between gap-x-4">
                    <h1 className={'text-xl font-medium leading-snug'}>Emails</h1>
                    <EducationalContentDialog.Root>
                        <EducationalContentDialog.Trigger>
                            About issues
                        </EducationalContentDialog.Trigger>
                        <EducationalContentDialog.Content>
                            <EducationalContentDialog.Header>
                                <EducationalContentDialog.Title>
                                    Types of email issues
                                </EducationalContentDialog.Title>
                                <p>Your data is separated into 2 types of issues:</p>
                            </EducationalContentDialog.Header>
                            <div className="flex flex-col gap-4">
                                <div className="flex gap-3 text-sm text-gray-600">
                                    <div className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-base text-red-600">
                                        <FontAwesomeIcon icon={faExclamationCircle} />
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <h3 className="font-medium text-gray-700">Invalid email</h3>
                                        <p>
                                            This email is not deliverable, possibly due to wrong
                                            information or a nonexistent email address, such as a
                                            string without a domain or containing other errors.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-3 text-sm text-gray-600">
                                    <div className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 text-base text-orange-600">
                                        <FontAwesomeIcon icon={faXmarkToSlot} />
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <h3 className="font-medium text-gray-700">Bounced email</h3>
                                        <p>
                                            A bounced email address is due to either the recipient's
                                            inbox being full, the email being blocked by the
                                            recipient, or the domain not existing.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </EducationalContentDialog.Content>
                    </EducationalContentDialog.Root>
                </div>
                <p className="text-sm text-gray-600">
                    Email addresses are routinely checked for validity and bounces via Engage
                    communications, receipts, or other notifications. Any email recognized as
                    bounced or undeliverable will appear in the table below for correction.
                </p>
            </div>

            <FilterProvider cacheKey={'dataHygieneEmails'} resource={'dataHygieneEmails'}>
                <Box>
                    <div className="@container">
                        <div className="flex items-center justify-between p-3">
                            <div className="flex gap-3">
                                <Input.Root>
                                    <Input.SearchIcon />
                                    <Input.Form
                                        placeholder={'Search'}
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </Input.Root>
                                <ToggleSaveForLaterButton
                                    savedForLater={savedForLater}
                                    toggleSavedForLater={toggleSavedForLater}
                                    count={metrics.data_hygiene.emails.saved_for_later}
                                />
                            </div>

                            <Table.ResultsCount fallbackTotal={emailsResultsCount} />
                        </div>
                        <Separator />
                        <div
                            className={
                                '@2xl:justify-between flex flex-wrap items-center justify-end gap-4 px-3 py-2'
                            }
                        >
                            <div className={'@2xl:w-auto flex w-full flex-wrap items-center gap-2'}>
                                <FilterGroupProvider
                                    showInnerOperator={false}
                                    showOuterOperator={false}
                                    defaultOperator="or"
                                >
                                    <QuickFiltersBar quickFilters={allFilters} />
                                </FilterGroupProvider>
                            </div>

                            <IgnoreButton.Root>
                                <IgnoreButton.Button
                                    size="sm"
                                    disabled={
                                        !selectedRows?.length || bulkIgnoreIsPending || isFetching
                                    }
                                    onClick={() => {
                                        bulkIgnore({
                                            ids: selectedRowsOriginal.map(x => x.id),
                                            row_ids: selectedRows.map(x => x.id),
                                        });
                                    }}
                                >
                                    {bulkIgnoreIsPending ? (
                                        <>
                                            <FontAwesomeIcon
                                                className="animate-spin"
                                                icon={faSpinnerThird}
                                            />
                                            Ignoring
                                        </>
                                    ) : (
                                        <>
                                            <span>Ignore selected</span>
                                            {selectedRows?.length && !isFetching ? (
                                                <CountBadge count={selectedRows.length} size="sm" />
                                            ) : null}
                                        </>
                                    )}
                                </IgnoreButton.Button>
                                <IgnoreButton.AlertContent>
                                    <IgnoreButton.AlertBody>
                                        <IgnoreButton.Title />
                                        <IgnoreButton.Description />
                                    </IgnoreButton.AlertBody>
                                    <IgnoreButton.AlertAction
                                        onClick={() => {
                                            bulkIgnore({
                                                ids: selectedRowsOriginal.map(x => x.id),
                                                row_ids: selectedRows.map(x => x.id),
                                            });
                                        }}
                                    />
                                </IgnoreButton.AlertContent>
                            </IgnoreButton.Root>
                        </div>
                        <Separator />
                    </div>

                    <Table.Root
                        isLoading={isPending}
                        table={table}
                        searchTerm={tableSearchTerm}
                        isFiltered={isFiltered}
                        variant="bordered"
                    >
                        <Table.Content>
                            <Table.Header />
                            <Table.Body className="align-top [&>tr>td>*]:h-auto [&>tr>td]:h-auto [&>tr>td]:px-6 [&>tr>td]:py-4" />
                        </Table.Content>
                        <DerivedTableEmpty />
                        <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                            <Table.PaginationPageNavigation />
                            <Table.PaginationPerPageDropdown pageSizes={HYGIENE_PAGE_SIZES} />
                        </Table.Pagination>
                    </Table.Root>
                </Box>
            </FilterProvider>
        </div>
    );
};

const EmailUpdateCell = (props: { email: DataHygieneEmail }) => {
    const { email } = props;
    const cellData = useMemo(() => ({ email }), [email]);

    return (
        <DataHygieneEmailContext.Provider value={cellData}>
            <EmailUpdate />
        </DataHygieneEmailContext.Provider>
    );
};

export { DataHygieneEmails };
